<script>
  import { onMount } from 'svelte';
  import dayjs from 'dayjs';

  import * as yup from 'yup';
  import { useLocation, useNavigate } from 'svelte-navigator';
  import { get } from 'svelte/store';

  import { Button, InlineNotification, DatePicker, DatePickerInput, TextInput } from 'carbon-components-svelte';

  import RestoreTable from './RestoreTable.svelte';
  import RequestModal from './RequestModal.svelte';
  import LoadingSpinner from '../../../components/LoadingSpinner.svelte';
  import UnsavedChangesGuard from '../../../components/UnsavedChangesGuard.svelte';

  import { ping } from '../../../services';

  const INITIAL_FORM_STATE = {
    path: '',
    from: '',
    to: '',
    lifetime: 15,
  };

  const tableHeaders = [
    { key: 'path', value: 'Path' },
    { key: 'from', value: 'Start Date' },
    { key: 'to', value: 'End Date' },
    { key: 'lifetime', value: 'Lifetime (days)' },
    { key: 'actions', value: '', empty: true },
  ];

  const schema = yup.object().shape({
    path: yup
      .string()
      .test('no-space', 'No space is allowed', (value) => /^\S*$/.test(value))
      .test('no-leading-slash', 'No leading slash is allowed', (value) => !value?.startsWith('/'))
      .test('no-trailing-slash', 'No trailing slash is allowed', (value) => !value?.endsWith('/'))
      .test(
        'correct-slashes',
        'Must contain one slash if bucket is "p-lseg-ll-feeds", otherwise two slashes (e.g., bucket/nic/feed)',
        (value) => {
          const [bucketName] = value.split('/');
          if (!bucketName) {
            return false;
          }

          const REQUIRED_SLASHES = bucketName === 'p-lseg-ll-feeds' ? 1 : 2;
          return (value?.match(/\//g) || []).length === REQUIRED_SLASHES;
        }
      )
      .test('no-double-slash', 'No double slash is allowed', (value) => !value?.includes('//'))
      .test('invalid-bucket-name', 'Invalid bucket name; should start with "p-lseg-ll-"', (value) => {
        /* eslint-disable-next-line no-unsafe-optional-chaining */
        const [bucketName] = value?.split('/');
        return /^(p-lseg-ll-|d-lseg-ll-)[a-z0-9.-]+$/.test(bucketName);
      }),
    from: yup
      .date()
      .typeError('A valid date is required')
      .required('Start date is required')
      .max(new Date(), 'Start date cannot be in the future'),
    to: yup
      .date()
      .typeError('A valid date is required')
      .required('End date is required')
      .max(new Date(), 'End date cannot be in the future'),
    lifetime: yup
      .number()
      .default(15)
      .typeError('A valid number between 5 and 90 is required')
      .min(5, 'Lifetime must be at least 5 days')
      .max(90, 'Lifetime cannot exceed 90 days')
      .when('path', {
        is: (path) => !path.startsWith('p-lseg-ll-feeds'),
        then: (s) => s.required('Lifetime is required for raw feeds paths'),
        otherwise: (s) => s.notRequired(),
      }),
  });

  const location = useLocation();
  const navigate = useNavigate();
  const { state: locationState } = get(location);
  const { selectedEntitlements = [] } = locationState || {};

  let paths = selectedEntitlements.length ? [...selectedEntitlements] : [];
  let manualForm = { ...INITIAL_FORM_STATE },
    errors = {},
    modals = { request: { open: false } },
    reset = 0,
    hasAccess,
    guard;

  async function addPath() {
    const valid = await schema.validate(manualForm).catch((error) => {
      errors = { ...errors, [error.path]: error.message };
      return false;
    });

    if (!valid) {
      return;
    }

    const alreadyExists = paths.some((feed) => {
      return feed.path === manualForm.path && feed.from === manualForm.from && feed.to === manualForm.to;
    });

    if (!alreadyExists) {
      paths = [{ id: `${reset}`, ...manualForm }, ...paths];
    }

    manualForm = { ...INITIAL_FORM_STATE };
    reset += 1;
  }

  function removePath(id) {
    return function onRemove() {
      paths = paths.filter((path) => path.id !== id);
    };
  }

  function onPathChangeEvent(event) {
    const value = event.detail ?? '';

    let path = value;
    let from = manualForm.from;
    let to = manualForm.to;
    let dateErrors = {};

    // Match the date string    path    yyyy  mm (optional) dd (optional)
    const match = value.match(/^(.*?)\/(\d{4})(?:\/(\d{2}))?(?:\/(\d{2}))?\/?$/);
    if (match) {
      const [, basePath, yyyy, mm, dd] = match;
      path = basePath;
      const startDate = dayjs(`${yyyy}-${mm || '01'}-${dd || '01'}`);
      from = startDate.format('YYYY-MM-DD');
      to = startDate.endOf(dd ? 'day' : mm ? 'month' : 'year').format('YYYY-MM-DD');
      dateErrors = { from: undefined, to: undefined };
    }

    manualForm = { ...manualForm, path, from, to };

    if (value) {
      errors = { ...errors, path: undefined, ...dateErrors };
    }
  }

  function onDateChangeEvent(event) {
    const dateStr = event.detail.dateStr;
    manualForm = { ...manualForm, ...dateStr };

    if (dateStr.from) {
      errors = { ...errors, from: undefined };
    }
    if (dateStr.to) {
      errors = { ...errors, to: undefined };
    }
  }

  function onLifetimeChangeEvent(event) {
    const value = event.detail;
    manualForm = { ...manualForm, lifetime: value };

    if (value) {
      errors = { ...errors, lifetime: undefined };
    }
  }

  async function validateAccess() {
    try {
      await ping('/api/aws/s3/request');
      hasAccess = true;
    } catch (error) {
      hasAccess = false;
    }
  }

  function openModal(name) {
    modals = {
      ...modals,
      [name]: { open: true },
    };
  }

  function closeModal(name) {
    modals = {
      ...modals,
      [name]: { open: false },
    };
  }

  function onRequestSuccess({ detail }) {
    const target = `/object-restore-request/${detail.id}`;
    closeModal('request');
    guard.allowNext(target);
    navigate(target);
  }

  $: hasUnsavedWork = paths.length > 0;

  onMount(validateAccess);
</script>

{#if hasAccess === undefined}
  <LoadingSpinner loading />
{:else if !hasAccess}
  <InlineNotification
    kind="error"
    lowContrast
    title="Access Denied:"
    subtitle="You do not have access to the Object Restore module."
    hideCloseButton
  />
{:else}
  <div class="wrapper">
    <div class="controls">
      <h2>Path selection</h2>
      <TextInput
        required
        autocomplete="off"
        name="path"
        labelText="Path"
        placeholder="<bucket_name>/<nic?>/<feed>"
        invalidText={errors?.path}
        invalid={!!errors?.path}
        value={manualForm.path}
        on:change={onPathChangeEvent}
      />
      {#key reset}
        <DatePicker
          datePickerType="range"
          on:change={onDateChangeEvent}
          valueFrom={manualForm.from}
          valueTo={manualForm.to}
          dateFormat="Y-m-d"
          maxDate={new Date()}
        >
          <DatePickerInput
            labelText="Start date"
            placeholder="yyyy-mm-dd"
            required
            name="from"
            invalidText={errors?.from}
            invalid={!!errors.from}
            autocomplete="off"
          />
          <DatePickerInput
            labelText="End date"
            placeholder="yyyy-mm-dd"
            required
            name="to"
            invalidText={errors?.to}
            invalid={!!errors.to}
            autocomplete="off"
          />
        </DatePicker>
      {/key}
      <TextInput
        type="number"
        name="lifetime"
        labelText="Lifetime (days)"
        placeholder="5-90"
        invalidText={errors?.lifetime}
        invalid={!!errors?.lifetime}
        value={manualForm.lifetime}
        on:change={onLifetimeChangeEvent}
      />
      <Button on:click={addPath} size="field">Add</Button>
    </div>
    <div class="results">
      <h2>Restore</h2>
      <Button disabled={!paths.length} size="small" on:click={() => openModal('request')}>Request Restore</Button>
      <RestoreTable {paths} {tableHeaders} {removePath} />
    </div>
  </div>
{/if}

{#if modals.request.open}
  <RequestModal open {paths} on:success={onRequestSuccess} on:close={() => closeModal('request')} />
{/if}

<UnsavedChangesGuard bind:this={guard} when={hasUnsavedWork}>
  <p>You have unsubmitted restore paths. If you leave, your path selections will be lost.</p>
</UnsavedChangesGuard>

<style>
  .wrapper {
    display: flex;
    flex-direction: row;
  }

  .controls :global(.bx--form-item) {
    padding-bottom: 0.5rem;
  }

  .controls {
    width: 30%;
    margin-right: 0.5rem;
    position: sticky;
    top: 1rem;
    align-self: flex-start;
    z-index: 1;
  }

  .results {
    width: 70%;
    margin-left: 0.5rem;
  }

  .results :global(.bx--btn.bx--btn--primary) {
    width: 100%;
    max-width: unset;
    justify-content: center;
    position: sticky;
    top: 0.5rem;
    z-index: 1;
    padding: 0.25rem 1rem;
  }

  .controls :global(.bx--label) {
    margin-bottom: 0.25rem;
  }

  .wrapper h2 {
    font-size: 1.25rem;
    font-weight: 400;
    letter-spacing: 0;
    line-height: 1.4;
    margin-bottom: 1rem;
  }

  .controls :global(.bx--text-input) {
    max-width: 20rem;
  }

  .controls :global(.flatpickr-calendar.open) {
    z-index: 2;
  }

  .controls :global(.bx--btn.bx--btn--primary) {
    justify-content: center;
    margin-top: 1rem;
  }

  @media (max-width: 1024px) {
    .wrapper {
      flex-wrap: wrap;
    }

    .controls {
      width: 100%;
      margin-right: 0;
      margin-bottom: 0.5rem;
      position: unset;
      top: 0;
      z-index: 2;
    }

    .results {
      width: 100%;
      margin-left: 0;
      margin-top: 0.5rem;
    }
  }
</style>
